// Apolo IDE — ToolCallBlock (S+A Tier chat refresh).
//
// Renderiza UMA invocação de ferramenta (call + result fundidos) como um
// cartão colapsável estilo Claude Code / Cursor, no lugar das paredes de texto
// cru que o Code tab mostrava (run_command com ANSI literal "[90m[39m").
//
//   • Header 1-linha: ícone por tool + comando/path mono truncado + duração +
//     badge de status (executando / exit 0 / exit N / negado).
//   • Fechado por padrão; abre sozinho em erro/negado pra não esconder falha.
//   • Body por tipo de tool:
//       run_command/list → terminal mono (ANSI limpo via stripAnsi)
//       write_file       → FilePreview com syntax highlight + acento verde
//       read_file        → FilePreview neutro
//       grep_search      → GrepResults (agrupado por arquivo)
//   • Clamp ~16 linhas + "mostrar mais" (mantém o output inteiro no DOM) + copiar.
//
// Mensagem (de SessionWorkspace.runAgent, já fundida):
//   { role:'tool', tool_name, tool_args, content, result, status, duration_ms }
//   status ∈ 'running' | 'done' | 'denied'.

import { memo, useEffect, useState } from 'react';
import {
  Ban, Check, ChevronDown, ChevronRight, CircleCheck, CircleX,
  Copy, Square, Wrench,
} from 'lucide-react';
import { stripAnsi } from '../../../lib/stripAnsi.js';
import FilePreview from './FilePreview.jsx';
import GrepResults from './GrepResults.jsx';
import DiffCard from './DiffCard.jsx';
import FileCard from './FileCard.jsx';
import AttachmentActions from './AttachmentActions.jsx';
import { countLines } from '../../../lib/artifacts.js';
import { useAgentStore } from '../../../store/useAgentStore.js';
// harness-library-uplift E2 — TOOL_META saiu do inline (6 de ~25 tools nomeadas;
// o resto caía na chave-inglesa genérica) pro registry unificado toolCatalog.js
// (25+ tools com nome/ícone/descrição — mesma fonte da aba Tools do catálogo).
import { TOOL_CATALOG } from '../../../lib/toolCatalog.js';

const TOOL_META = TOOL_CATALOG;

const LIMIT = 16; // linhas visíveis antes do "mostrar mais"

function fmtChars(n) {
  if (n == null || Number.isNaN(n)) return null;
  return n < 1000 ? `${n} chars` : `${(n / 1000).toFixed(1)}k chars`;
}

function fmtDuration(ms) {
  if (ms == null || Number.isNaN(ms)) return null;
  if (ms < 1000) return `${ms}ms`;
  return ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms / 1000)}s`;
}

// Alvo no header (1 linha). Comando multilinha (heredoc) → 1ª linha + reticências.
function headerTarget(toolName, args, fallback) {
  const meta = TOOL_META[toolName];
  if (!meta) return fallback || toolName;
  const raw = String(args?.[meta.field] ?? '');
  if (!raw) return toolName === 'list_files' ? '(raiz)' : fallback || meta.verb;
  const first = raw.split('\n')[0];
  return raw.includes('\n') ? `${first} …` : first;
}

// Texto completo do body por tool, já limpo de ANSI (base pro clamp + copy).
function buildFullText(toolName, args, rawResult, status) {
  if (status === 'denied') return 'Ação negada pelo usuário.';
  if (status === 'aborted') return 'Interrompido.';
  if (status === 'running') return '';
  const raw = String(rawResult ?? '');
  if (toolName === 'run_command') {
    const body = raw.replace(/^exitCode:\s*-?\d+\n?/, ''); // exitCode vai pro badge
    return stripAnsi(body).trimEnd() || '(sem saída)';
  }
  if (toolName === 'write_file') {
    if (/^Erro/i.test(raw)) return stripAnsi(raw);
    return stripAnsi(String(args?.content ?? '')) || '(arquivo vazio)';
  }
  return stripAnsi(raw) || '(sem saída)';
}

function CopyButton({ getText, label, className }) {
  const [copied, setCopied] = useState(false);
  async function copy(e) {
    e.stopPropagation();
    const text = getText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button type="button" onClick={copy} aria-label={label} title={label} className={className}>
      {copied ? <Check size={13} className="text-apolo-success" aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
    </button>
  );
}

function StatusBadge({ status, exitCode, isError }) {
  if (status === 'running') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] text-apolo-textTertiary">
        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-apolo-accent" aria-hidden="true" />
        executando
      </span>
    );
  }
  if (status === 'denied') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-apolo-warning">
        <Ban size={13} aria-hidden="true" /> negado
      </span>
    );
  }
  if (status === 'aborted') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-apolo-warning">
        <Square size={11} aria-hidden="true" /> interrompido
      </span>
    );
  }
  if (isError) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-apolo-error">
        <CircleX size={13} aria-hidden="true" /> {exitCode != null ? `exit ${exitCode}` : 'erro'}
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-apolo-success">
      <CircleCheck size={13} aria-hidden="true" /> {exitCode != null ? `exit ${exitCode}` : 'ok'}
    </span>
  );
}

// Corpo por tipo de tool. `shownText` já vem clampado.
function ToolBody({ toolName, args, shownText, isError, status, fullLineCount }) {
  if (status === 'running') {
    return <p className="px-3 py-2 font-mono text-[12px] italic text-apolo-textTertiary">Executando…</p>;
  }
  // harness-library-uplift E2 — card dedicado do load_skill: o playbook inteiro
  // NÃO é despejado no feed (só vai pro contexto do modelo). O card diz o que
  // aconteceu; o conteúdo continua disponível via botão de copiar do header.
  if (!isError && toolName === 'load_skill') {
    return (
      <p className="px-3 py-2 text-[12px] text-apolo-textSecondary">
        Carregou a skill <span className="font-mono font-medium text-apolo-text">{String(args?.name || '(sem nome)')}</span>
        {' '}no contexto do modelo{fullLineCount > 0 ? ` — playbook de ${fullLineCount} ${fullLineCount === 1 ? 'linha' : 'linhas'}` : ''}.
        <span className="mt-1 block text-[11px] text-apolo-textTertiary">O conteúdo orienta o agente; use o botão de copiar pra inspecioná-lo.</span>
      </p>
    );
  }
  if (!isError && toolName === 'write_file') {
    return <FilePreview path={args.path} content={shownText} variant="write" />;
  }
  if (!isError && toolName === 'read_file') {
    return <FilePreview path={args.path} content={shownText} variant="read" />;
  }
  if (!isError && toolName === 'grep_search') {
    return <GrepResults text={shownText} />;
  }
  return (
    <pre className="overflow-auto whitespace-pre-wrap break-words bg-apolo-codeBg px-3 py-2 font-mono text-[12px] leading-relaxed text-apolo-textSecondary">
      {shownText}
    </pre>
  );
}

const ACCENT = {
  running: 'border-l-apolo-accent',
  // ok/error: sem barra colorida — o status já é dito pelo badge à direita
  // (ícone + rótulo colorido), então a borda esquerda fica neutra e calma.
  ok: 'border-l-apolo-borderSubtle',
  error: 'border-l-apolo-borderSubtle',
  denied: 'border-l-apolo-warning',
};

// E2-WS3 — memoized: a finished tool message keeps a stable object ref
// (segmentTurns reuses message refs; only the live message gets a new object),
// so a streamed token no longer re-renders every prior tool card's output subtree.
function ToolCallBlock({ message, density = 'comfortable' }) {
  const compact = density === 'compact';
  // design-uplift E1 — "Detailed" density (Cursor parity): cards open by
  // default and output is shown un-clamped. Layout otherwise = comfortable.
  const detailed = density === 'detailed';
  const toolName = message.tool_name || 'tool';
  const args = message.tool_args || {};
  // E1 (Camada V) — fidelidade no reload: msgs persistidas não têm `result`,
  // só `metadata.summary` (agora capado em 4000, não 500). Cai nele para o
  // body mostrar a saída real em vez de "(sem saída)".
  const rawResult = message.result ?? message.metadata?.summary ?? '';
  // Reload fix (harness-audit E2): persisted tool messages carry NO live
  // `status`/`result` — only `metadata` (buildToolPayload already writes
  // `status` + `exit_code` there). Without the metadata fallback the status
  // resolved to 'running' forever (pulsing "executando" on every revisit) and
  // the rich DiffCard/FileCard branches below — gated on status==='done' —
  // never fired on reload.
  const status = message.status || message.metadata?.status || (message.result != null ? 'done' : 'running');
  // root da sessão ativa — pro AttachmentActions ler bytes (imagem/binário) do
  // disco quando o conteúdo de texto não está na mensagem (ex.: após reload).
  const sessionRoot = useAgentStore((s) => {
    const id = s.code?.activeSessionId;
    return s.code?.sessions?.find((x) => x.id === id)?.directory || undefined;
  });

  const meta = TOOL_META[toolName] || { icon: Wrench, verb: toolName };
  const Icon = meta.icon;

  const exitMatch = String(rawResult).match(/^exitCode:\s*(-?\d+)/);
  // On reload rawResult is empty; recover the exit code from persisted metadata
  // so a failed command still renders the error styling instead of a bare "ok".
  const exitCode = exitMatch ? Number(exitMatch[1]) : (message.metadata?.exit_code ?? null);
  const isDenied = status === 'denied';
  const isAborted = status === 'aborted';
  const isRunning = status === 'running';
  const isError =
    !isDenied && !isAborted && !isRunning &&
    ((exitCode != null && exitCode !== 0) || /^Erro/i.test(String(rawResult)));
  const accentKey = isRunning ? 'running' : isDenied ? 'denied' : isAborted ? 'denied' : isError ? 'error' : 'ok';

  const target = headerTarget(toolName, args, message.content);
  const fullText = buildFullText(toolName, args, rawResult, status);
  const lines = fullText ? fullText.split('\n') : [];
  // Detailed never clamps — show the full output (no "mostrar mais" gate).
  // load_skill não despeja corpo no feed (card dedicado) ⇒ nunca mostra o expander.
  const long = !detailed && toolName !== 'load_skill' && lines.length > LIMIT;

  const [expanded, setExpanded] = useState(false);
  const shownText = !expanded && long ? lines.slice(0, LIMIT).join('\n') : fullText;

  // Meta no header: duração + (tamanho do write OU nº de linhas do output).
  const duration = fmtDuration(message.duration_ms);
  let sizeMeta = null;
  if (!isRunning) {
    if (toolName === 'write_file' && !isError) {
      const n = args.content != null
        ? String(args.content).length
        : Number(String(rawResult).match(/\((\d+)\s*chars?/)?.[1] ?? NaN);
      sizeMeta = fmtChars(n);
    } else if (fullText && fullText !== '(sem saída)') {
      sizeMeta = `${lines.length} ${lines.length === 1 ? 'linha' : 'linhas'}`;
    }
  }

  const [open, setOpen] = useState(() => {
    if (detailed) return true;
    if (compact) {
      try { return localStorage.getItem('apolo:chatToolCardsCollapsed') !== 'true'; } catch { return false; }
    }
    return false;
  });
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if ((isError || isDenied) && !autoOpened) {
      setOpen(true);
      setAutoOpened(true);
    }
  }, [isError, isDenied, autoOpened]);
  // Switching to "Detailed" mid-session opens cards (one-way, like the error
  // auto-open); leaving Detailed never force-closes — the user still wins.
  useEffect(() => {
    if (detailed) setOpen(true);
  }, [detailed]);

  const showBody = open && (fullText || isRunning);
  const statusLabel = isRunning ? 'executando' : isDenied ? 'negado' : isAborted ? 'interrompido'
    : isError ? `falhou${exitCode != null ? ` (exit ${exitCode})` : ''}` : 'ok';

  // E3 — write_file concluído (sem erro) vira um DiffCard rico; Revisar abre o
  // Diff. Erros/running mantêm o cartão de tool normal (mostra a falha).
  if (toolName === 'write_file' && status === 'done' && !isError) {
    const md = message.metadata && typeof message.metadata === 'object' ? message.metadata : null;
    const dcPath = md?.path ?? args.path;
    if (dcPath) {
      return (
        <div className="flex flex-col gap-1.5">
          <DiffCard
            path={dcPath}
            additions={md ? md.additions ?? 0 : countLines(args.content)}
            deletions={md ? md.deletions ?? 0 : 0}
            status={status}
            diffText={md ? undefined : args.content}
            onRevisar={() => window.dispatchEvent(new CustomEvent('apolo:openDiff', { detail: { path: dcPath } }))}
          />
          {/* Fase 2 — copiar/baixar o arquivo que a IA escreveu (qualquer formato). */}
          <AttachmentActions path={dcPath} content={args.content} root={sessionRoot} className="pl-1" />
        </div>
      );
    }
  }

  // E3 (v2) — read_file concluído vira o FileCard azul ("arquivo apresentado no
  // chat"). Conteúdo fica atrás de "Ver conteúdo" (nada se perde). Erros/running
  // caem no cartão de tool normal abaixo.
  if (toolName === 'read_file' && status === 'done' && !isError && args.path) {
    return (
      <div className="flex flex-col gap-1.5">
        <FileCard path={args.path} content={fullText} />
        {/* Fase 2 — copiar/baixar o arquivo lido (qualquer formato). */}
        <AttachmentActions path={args.path} content={fullText} root={sessionRoot} className="pl-1" />
      </div>
    );
  }

  return (
    <div className={`rx-toolcard group/tool overflow-hidden rounded-apolo-md border border-l-2 border-apolo-borderSubtle ${ACCENT[accentKey]} bg-apolo-surface1`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${meta.verb} — ${statusLabel}`}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-apolo-surface2/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-apolo-accent/40"
      >
        {open ? (
          <ChevronDown size={13} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />
        ) : (
          <ChevronRight size={13} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />
        )}
        <Icon size={14} className="shrink-0 text-apolo-textSecondary" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-apolo-textSecondary" title={target}>
          {target}
        </span>
        {(duration || sizeMeta) && (
          <span className="hidden shrink-0 items-center gap-1.5 text-[10px] tabular-nums text-apolo-textTertiary sm:inline-flex">
            {duration && <span>{duration}</span>}
            {duration && sizeMeta && <span aria-hidden="true" className="text-apolo-textDisabled">·</span>}
            {sizeMeta && <span>{sizeMeta}</span>}
          </span>
        )}
        <StatusBadge status={status} exitCode={exitCode} isError={isError} />
      </button>

      {showBody && (
        <div className={`relative animate-fadeSlide border-t border-apolo-borderSubtle ${detailed ? 'max-h-[480px] overflow-auto' : ''}`}>
          {!isRunning && fullText && (
            <CopyButton
              getText={() => fullText}
              label="Copiar saída"
              className="absolute right-1.5 top-1.5 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2/80 text-apolo-textTertiary opacity-60 backdrop-blur transition hover:text-apolo-textPrimary focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing group-hover/tool:opacity-100 sm:opacity-0"
            />
          )}
          <ToolBody toolName={toolName} args={args} shownText={shownText} isError={isError} status={status} fullLineCount={lines.length} />
          {long && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="flex w-full items-center justify-center gap-1 border-t border-apolo-borderSubtle bg-apolo-surface1 py-1 text-[11px] font-medium text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textSecondary"
            >
              {expanded ? 'Mostrar menos' : `Mostrar mais (${lines.length} linhas)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(ToolCallBlock);
